// --------------------------------------------------------------
// Created On: 2023-01-27
// Author: Zachary Thomas
//
// Last Modified: 2026-03-31
// Modified By: Dimitra Weinstein
//
// Copyright 2025 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, Fragment, useMemo, useEffect } from "react";
import useApi from "../../../hooks/useApi";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import SaveChangesModal from "../../../components/SaveChangesModal/SaveChangesModal";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import Error from "../../../components/Error/Error";
import Spinner from "../../../components/Spinner/Spinner";
import ReportAttributeForm from "./ReportAttributeForm/ReportAttributeForm";
import ReportEmailForm from "../ReportEmailForm/ReportEmailForm";
import ReportAssetgroupForm from "./ReportAssetgroupForm/ReportAssetgroupForm";
import ScheduleForm from "../ScheduleForm/ScheduleForm";
import useReduxSelector from "../../../hooks/useReduxSelector";
import deepCopy from "../../../utilities/deepCopy";
import IconTooltip from "../../../components/IconTooltip/IconTooltip";
import apiRequest from "../../../utilities/api/apiRequest";
import getApiError from "../../../utilities/api/getApiError";
import scheduleFromLocalToUtc from "../../../utilities/time/scheduleFromLocalToUtc";
import scheduleFromUtcToLocal from "../../../utilities/time/scheduleFromUtcToLocal";
import {
  API,
  MAX_REPORT_NAME_LENGTH,
  MAX_REPORT_ATTRIBUTES,
  REPORT_MODE_CURRENT,
  REPORT_MODE_DIFFERENCE,
  REPORT_ASSET_ALL,
  REPORT_ASSET_BY_GROUP,
  SCHEDULE_TYPE_DAILY,
  VALID_EMAIL_REGULAR_EXPRESSION,
} from "../../../constants/miscellaneous";
import userHasCrossCompanyAccess from "../../../utilities/userHasCrossCompanyAccess";
import { REPORT_TYPES } from "../../../constants/reducerActions";
import userHasPermission from "../../../utilities/userHasPermission";
import {
  CREATE_REPORTS_PERMISSION,
  DELETE_REPORTS_PERMISSION,
  UPDATE_REPORTS_PERMISSION,
} from "../../../constants/permissions";
import styles from "./ReportModal.module.scss";
import InfoBanner from "src/components/InfoBanner/InfoBanner";

// Modal for creating, updating, or deleting a report.
export default function ReportModal(props: Props): Component {
  const initialReport: AssetReport = {
    reportId: 0,
    name: "",
    enabled: true,
    type: REPORT_MODE_CURRENT,
    hours: 1,
    attributes: [],
    assetgroups: [],
    emailAddresses: [],
    scheduleSettings: [{ reportScheduleId: 1, type: SCHEDULE_TYPE_DAILY, value: "09:00" }],
    isAssetRunning: false,
    thresholds: {},
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showConfirmExit, setShowConfirmExit] = useState<boolean>(false);
  const [previousReport, setPreviousReport] = useState<AssetReport>(initialReport);
  const [name, setName] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [type, setType] = useState<string>(REPORT_MODE_CURRENT);
  const [assetSelectionType, setAssetSelectionType] = useState<string>(REPORT_ASSET_ALL);
  const [hours, setHours] = useState<number>(1);
  const [attributes, setAttributes] = useState<FormAttribute[]>([]);
  const [assetgroups, setAssetgroups] = useState<FormAssetgroup[]>([]);
  const [emailAddresses, setEmailAddresses] = useState<FormEmailAddress[]>([]);
  const [scheduleSettings, setScheduleSettings] = useState<ReportSchedule[]>(initialReport.scheduleSettings);
  const [isAssetRunning, setIsAssetRunning] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const userHasFullReportTypeAccess = useMemo(
    () => getUserHasFullReportTypeAccess(props.reportTypes),
    [props.reportTypes]
  );
  const allowsAttributes = useMemo(() => getAllowsAttributes(type, props.reportTypes), [type, props.reportTypes]);
  const allowsComparators = useMemo(() => getAllowsComparators(type, props.reportTypes), [type, props.reportTypes]);
  const allowsAssetRunning = useMemo(() => getAllowsAssetRunning(type, props.reportTypes), [type, props.reportTypes]);
  const allowsNonNumerical = useMemo(() => getAllowsNonNumerical(type, props.reportTypes), [type, props.reportTypes]);

  // Get detailed report information from the API.
  useApi(
    () => {
      setLoading(props.reportId > 0);
      return props.reportId > 0;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/report/${props.reportId}`,
    },
    async (response: Response, responseBody: GetResponseBody) => {
      if (response.ok && responseBody) {
        const scheduleSettings = scheduleFromUtcToLocal(responseBody.scheduleSettings);
        if (scheduleSettings !== null) {
          const report = {
            reportId: responseBody.reportId,
            name: responseBody.name,
            enabled: responseBody.enabled,
            type: responseBody.type,
            hours: responseBody.hours,
            attributes: responseBody.attributes,
            assetgroups: responseBody.assetgroups,
            emailAddresses: responseBody.emailAddresses,
            scheduleSettings: scheduleSettings,
            isAssetRunning: responseBody.isAssetRunning,
            thresholds: responseBody.thresholds || {},
          };
          const formAssetgroups: FormAssetgroup[] = responseBody.assetgroups.map((assetgroup, i) => {
            return { formId: i + 1, assetgroupId: assetgroup.assetgroupId, name: assetgroup.name };
          });
          const formEmailAddresses: FormEmailAddress[] = responseBody.emailAddresses.map((emailAddress, i) => {
            return { formId: i + 1, value: emailAddress };
          });
          const formAttributes: FormAttribute[] = responseBody.attributes.map((attribute, i) => {
            return { formId: i + 1, attributeId: attribute.attributeId, name: attribute.name };
          });

          // For each threshold, add the comparator and value to the associated attribute if it has one.
          // NOTE: When an attribute is found in formAttributes, it is updated in place (see associatedAttribute below),
          // which is why formAttributes is not explicitly updated anywhere.
          if (
            responseBody.thresholds !== undefined &&
            responseBody.thresholds !== null &&
            Object.keys(responseBody.thresholds).length > 0
          ) {
            Object.entries(responseBody.thresholds).forEach(([attributeId, threshold]) => {
              const formattedThreshold: Threshold = threshold as Threshold;
              const associatedAttribute = formAttributes.find(
                (attribute) => attribute.attributeId === parseInt(attributeId, 10)
              );
              if (associatedAttribute !== undefined && associatedAttribute !== null) {
                const associatedComparator = props.comparators.find(
                  (comparator) =>
                    formattedThreshold.comparator !== undefined &&
                    formattedThreshold.comparator !== null &&
                    comparator.name.toLowerCase() === formattedThreshold.comparator.toLowerCase()
                );
                if (associatedComparator !== undefined && associatedComparator !== null) {
                  // Since associatedAttribute is found directly from the formAttributes list, the assignments below
                  // will directly update the formAttributes array.
                  associatedAttribute.comparatorId = associatedComparator.alertThresholdComparatorId;
                  associatedAttribute.value = String(formattedThreshold.value);
                }
              }
            });
          }

          let tempAssetSelectionType = REPORT_ASSET_ALL;
          if (!responseBody.allAssets) {
            tempAssetSelectionType = REPORT_ASSET_BY_GROUP;
          }
          setPreviousReport(report);
          setName(responseBody.name);
          setEnabled(responseBody.enabled);
          setType(responseBody.type);
          setHours(responseBody.hours);
          setAssetSelectionType(tempAssetSelectionType);
          setAttributes(formAttributes);
          setAssetgroups(formAssetgroups);
          setEmailAddresses(formEmailAddresses);
          setScheduleSettings(scheduleSettings);
          setIsAssetRunning(responseBody.isAssetRunning);
          setErrorMessage("");
        } else {
          setErrorMessage("Internal server error. Unable to get report settings.");
        }
      } else {
        setErrorMessage("Internal server error. Unable to get report settings.");
      }
      setLoading(false);
    },
    [props.reportId]
  );

  // Cleans up attributes whenever the report type that is selected changes whether attributes and/or
  // threshold comparators are allowed or not.
  // Non-numerical attributes are also removed if the current report type does not allow them to be included
  // (e.g. Standard Statistics).
  useEffect(() => {
    if (allowsAttributes === true) {
      let attributesDeepCopy = deepCopy(attributes);

      // Filter out non-numerical attributes if the selected report type does not allow for it.
      const numericalAttributes: FormAttribute[] = [];
      if (allowsNonNumerical === false) {
        attributesDeepCopy.forEach((attribute) => {
          const foundAttribute = props.attributes.find((attr) => attr.attributeId === attribute.attributeId);
          if (
            foundAttribute !== undefined &&
            foundAttribute !== null &&
            foundAttribute.isBoolean === false &&
            (foundAttribute.regEnumId === undefined || foundAttribute.regEnumId === null)
          ) {
            // Only keep the attribute if it's not a boolean AND if it doesn't have a regEnumId.
            numericalAttributes.push(attribute);
          }
        });
        attributesDeepCopy = deepCopy(numericalAttributes);
      }

      if (allowsComparators === true) {
        // Clear out all the threshold comparators and values.
        attributesDeepCopy.forEach((attribute) => {
          if (attribute.comparatorId === undefined || attribute.comparatorId === null || attribute.comparatorId <= 0) {
            attribute.comparatorId = 0;
          }
          if (attribute.value === undefined || attribute.value === null || attribute.value.trim().length === 0) {
            attribute.value = "";
          }
        });
      } else if (allowsComparators === false) {
        // Clear all threshold comparator and value fields from each attribute if comparators aren't allowed for the report type.
        attributesDeepCopy.forEach((attribute) => {
          delete attribute.comparatorId;
          delete attribute.value;
        });
      }
      setAttributes(attributesDeepCopy);
    } else if (allowsAttributes === false) {
      // Clear all attributes if attributes aren't allowed for the report type.
      setAttributes([]);
    }
  }, [allowsAttributes, allowsComparators, allowsNonNumerical, props.attributes]);

  // Clear the isAssetRunning checkbox whenever the user selects a report type that does not allow collecting data only
  // when the asset is running.
  useEffect(() => {
    if (allowsAssetRunning === false) {
      setIsAssetRunning(false);
    }
  }, [allowsAssetRunning]);

  // Determine if the current user has access to all possible report types.
  // This is needed to determine if the premium feature info banner should display or not.
  function getUserHasFullReportTypeAccess(reportTypes: ReportType[]): boolean {
    if (reportTypes !== undefined && reportTypes !== null) {
      const userLacksFullAccess = reportTypes.some((reportType) => reportType.userHasAccess === false);
      return !userLacksFullAccess;
    } else {
      // Assume that the user does not have full access to all report types if we cannot get report type information.
      return false;
    }
  }

  // Determine if the selected report type allows attributes.
  function getAllowsAttributes(reportTypeName: string, reportTypes: ReportType[]): boolean {
    if (reportTypes !== undefined && reportTypes !== null) {
      const reportType = reportTypes.find((type) => type.code.toLowerCase() === reportTypeName.toLowerCase());
      if (reportType !== undefined && reportType !== null) {
        return reportType.allowsAttributes;
      }
    }
    // Assume that attributes are not allowed if no report types could be found or if we cannot find the report type.
    return false;
  }

  // Determine if the selected report type allows comparators.
  function getAllowsComparators(reportTypeName: string, reportTypes: ReportType[]): boolean {
    if (reportTypes !== undefined && reportTypes !== null) {
      const reportType = reportTypes.find((type) => type.code.toLowerCase() === reportTypeName.toLowerCase());
      if (reportType !== undefined && reportType !== null) {
        return reportType.allowsComparators;
      }
    }
    // Assume that comparators are not allowed if no report types could be found or if we cannot find the report type.
    return false;
  }

  // Determine if the selected report type allows collecting data only when the asset is running.
  function getAllowsAssetRunning(reportTypeName: string, reportTypes: ReportType[]): boolean {
    if (reportTypes !== undefined && reportTypes !== null) {
      const reportType = reportTypes.find((type) => type.code.toLowerCase() === reportTypeName.toLowerCase());
      if (reportType !== undefined && reportType !== null) {
        return reportType.allowsAssetRunning;
      }
    }
    // Assume that tracking data only when the asset is running is not allowed if no report types could be found
    // or if we cannot find the report type.
    return false;
  }

  // Determine if the selected report type allows collecting data on non-numerical attributes.
  function getAllowsNonNumerical(reportTypeName: string, reportTypes: ReportType[]): boolean {
    if (reportTypes !== undefined && reportTypes !== null) {
      const reportType = reportTypes.find((type) => type.code.toLowerCase() === reportTypeName.toLowerCase());
      if (reportType !== undefined && reportType !== null) {
        return reportType.allowsNonNumerical;
      }
    }
    // Assume that tracking data only when the asset is running is not allowed if no report types could be found
    // or if we cannot find the report type.
    return false;
  }

  // Gets all attributes with a numerical data type since those are the only attributes that can
  // be used for report types that only allow numerical attributes.
  function getFilteredAttributes(
    reportTypeName: string,
    reportTypes: ReportType[],
    attributes: Attribute[]
  ): Attribute[] {
    if (!getAllowsNonNumerical(reportTypeName, reportTypes)) {
      const filteredAttributes: Attribute[] = attributes.filter(
        (attribute) => !attribute.isBoolean && (attribute.regEnumId === undefined || attribute.regEnumId === null)
      );
      return filteredAttributes;
    } else {
      return attributes;
    }
  }

  // Exit modal if no changes have been made. Otherwise prompt the user.
  function exitModal(): void {
    // Get comparable current and previous email addresses.
    const emailAddressValues = emailAddresses.map((emailAddress) => emailAddress.value);
    let previousEmailAddressValues: string[] = [];
    if (previousReport.emailAddresses !== undefined) {
      previousEmailAddressValues = previousReport.emailAddresses;
    }

    // Get comparable current and previous attributes.
    const attributeIds = attributes.map((attribute) => attribute.attributeId);
    let previousAttributeIds: number[] = [];
    if (previousReport.attributes !== undefined) {
      previousAttributeIds = previousReport.attributes.map((attribute) => attribute.attributeId);
    }

    // Build the thresholds map for the current report.
    const thresholds: ThresholdMap = {};
    attributes.forEach((attribute) => {
      if (attribute.comparatorId !== undefined && attribute.comparatorId !== null) {
        const associatedComparator = props.comparators.find(
          (comparator) => comparator.alertThresholdComparatorId === attribute.comparatorId
        );
        if (associatedComparator !== undefined && associatedComparator !== null) {
          const threshold: Threshold = {
            comparator: associatedComparator.name,
            value: attribute.value !== undefined ? parseInt(attribute.value, 10) : 0,
          };
          thresholds[attribute.attributeId] = threshold;
        }
      }
    });

    // Get comparable current and previous asset groups.
    const assetgroupIds = assetgroups.map((assetgroup) => assetgroup.assetgroupId);
    let previousAssetgroupIds: number[] = [];
    if (previousReport.assetgroups !== undefined) {
      previousAssetgroupIds = previousReport.assetgroups.map((assetgroup) => assetgroup.assetgroupId);
    }

    // Get comparable current and previous schedules.
    const tempScheduleSettings = scheduleSettings.map((scheduleSetting, i) => {
      return {
        reportScheduleId: i + 1,
        type: scheduleSetting.type,
        value: scheduleSetting.value,
      };
    });
    let previousScheduleSettings: ReportSchedule[] = [];
    if (previousReport.scheduleSettings !== undefined) {
      previousScheduleSettings = previousReport.scheduleSettings.map((scheduleSetting, i) => {
        return {
          reportScheduleId: i + 1,
          type: scheduleSetting.type,
          value: scheduleSetting.value,
        };
      });
    }

    // Check to see if anything has changed in the report settings.
    if (
      name === previousReport.name &&
      enabled === previousReport.enabled &&
      type === previousReport.type &&
      hours === previousReport.hours &&
      JSON.stringify(thresholds) === JSON.stringify(previousReport.thresholds) &&
      JSON.stringify(emailAddressValues) === JSON.stringify(previousEmailAddressValues) &&
      JSON.stringify(attributeIds) === JSON.stringify(previousAttributeIds) &&
      JSON.stringify(assetgroupIds) === JSON.stringify(previousAssetgroupIds) &&
      JSON.stringify(tempScheduleSettings) === JSON.stringify(previousScheduleSettings)
    ) {
      // Since there have been no changes we can safely exit.
      discardChanges();
    } else {
      // We have unsaved changes, give the user a chance to save them.
      setShowConfirmExit(true);
    }
  }

  // Save changes.
  function saveChanges(): void {
    setShowConfirmExit(false);
    setErrorMessage("");
    if (props.isCreatingNewRecord) {
      void createReport();
    } else {
      void editReport(props.reportId);
    }
  }

  // Exit without saving changes.
  function discardChanges(): void {
    props.onClose();
    setShowConfirmDelete(false);
    setShowConfirmExit(false);
    setErrorMessage("");
  }

  // Add an empty email address.
  function addEmailAddress(): void {
    const emailAddressDeepCopy = deepCopy(emailAddresses);
    let formId = 1;
    emailAddressDeepCopy.forEach((emailAddress) => {
      if (emailAddress.formId >= formId) {
        formId = emailAddress.formId + 1;
      }
    });
    const emailAddress = {
      formId: formId,
      value: "",
    };
    emailAddressDeepCopy.push(emailAddress);
    setEmailAddresses(emailAddressDeepCopy);
  }

  // Update an email address.
  function updateEmailAddress(formId: number, value: string): void {
    const emailAddressDeepCopy = deepCopy(emailAddresses);
    const emailAddress = emailAddressDeepCopy.find((emailAddress) => emailAddress.formId === formId);
    if (emailAddress !== undefined) {
      emailAddress.value = value;
      setEmailAddresses(emailAddressDeepCopy);
    }
  }

  // Delete an email address.
  function deleteEmailAddress(formId: number): void {
    const emailAddressesDeepCopy = deepCopy(emailAddresses);
    const index = emailAddressesDeepCopy.findIndex((emailAddress) => emailAddress.formId === formId);
    if (index >= 0) {
      emailAddressesDeepCopy.splice(index, 1);
      setEmailAddresses(emailAddressesDeepCopy);
    }
  }

  // Add an empty attribute.
  function addAttribute(): void {
    const attributesDeepCopy = deepCopy(attributes);
    let formId = 1;
    attributesDeepCopy.forEach((attribute) => {
      if (attribute.formId >= formId) {
        formId = attribute.formId + 1;
      }
    });
    const attribute: FormAttribute = {
      formId: formId,
      attributeId: 0,
      name: "",
    };
    if (allowsComparators) {
      attribute.comparatorId = 0;
    }
    attributesDeepCopy.push(attribute);
    setAttributes(attributesDeepCopy);
  }

  // Update an attribute.
  function updateAttribute(
    formId: number,
    attributeName: string,
    comparatorId: number | undefined,
    value: string | undefined
  ): void {
    const attributesDeepCopy = deepCopy(attributes);
    const attribute = attributesDeepCopy.find((attribute) => attribute.formId === formId);
    if (attribute !== undefined) {
      const lookUpAttribute = props.attributes.find(
        (lookUpAttribute) => lookUpAttribute.name.toLowerCase() === attributeName.toLowerCase()
      );
      if (lookUpAttribute !== undefined) {
        attribute.attributeId = lookUpAttribute.attributeId;
        attribute.name = lookUpAttribute.name;
        attribute.comparatorId = comparatorId;
        attribute.value = value;
      } else {
        // Clear the threshold comparator and value if the attribute name is cleared.
        attribute.attributeId = 0;
        attribute.name = attributeName;
        attribute.comparatorId = 0;
        attribute.value = "";
      }
      setAttributes(attributesDeepCopy);
    }
  }

  // Delete an attribute.
  function deleteAttribute(formId: number): void {
    const attributesDeepCopy = deepCopy(attributes);
    const index = attributesDeepCopy.findIndex((attribute) => attribute.formId === formId);
    if (index >= 0) {
      attributesDeepCopy.splice(index, 1);
      setAttributes(attributesDeepCopy);
    }
  }

  // Add an empty asset group.
  function addAssetgroup(): void {
    const assetgroupsDeepCopy = deepCopy(assetgroups);
    let formId = 1;
    assetgroupsDeepCopy.forEach((assetgroup) => {
      if (assetgroup.formId >= formId) {
        formId = assetgroup.formId + 1;
      }
    });
    const assetgroup = {
      formId: formId,
      assetgroupId: 0,
      name: "",
    };
    assetgroupsDeepCopy.push(assetgroup);
    setAssetgroups(assetgroupsDeepCopy);
  }

  // Update an asset group.
  function updateAssetgroup(formId: number, name: string): void {
    const assetgroupsDeepCopy = deepCopy(assetgroups);
    const assetgroup = assetgroupsDeepCopy.find((assetgroup) => assetgroup.formId === formId);
    if (assetgroup !== undefined) {
      const lookUpAssetgroup = props.assetgroups.find(
        (lookUpAssetgroup) => lookUpAssetgroup.name.toLowerCase() === name.toLowerCase()
      );
      if (lookUpAssetgroup !== undefined) {
        assetgroup.assetgroupId = lookUpAssetgroup.assetgroupId;
      } else {
        assetgroup.assetgroupId = 0;
      }
      assetgroup.name = name;
      setAssetgroups(assetgroupsDeepCopy);
    }
  }

  // Delete an asset group.
  function deleteAssetgroup(formId: number): void {
    const assetgroupsDeepCopy = deepCopy(assetgroups);
    const index = assetgroupsDeepCopy.findIndex((assetgroup) => assetgroup.formId === formId);
    if (index >= 0) {
      assetgroupsDeepCopy.splice(index, 1);
      setAssetgroups(assetgroupsDeepCopy);
    }
  }

  // Check if the current report is valid.
  function reportIsValid(): boolean {
    // Check each element of arrays to see if an attribute was not correctly entered by the user.
    let invalidEmailIndex = -1;
    let invalidAttributeIndex = -1;
    let invalidNumericalAttributeIndex = -1;
    let invalidAssetgroupIndex = -1;
    let invalidComparatorIndex = -1;
    let invalidThresholdValueIndex = -1;
    emailAddresses.forEach((emailAddress, i) => {
      if (!VALID_EMAIL_REGULAR_EXPRESSION.test(emailAddress.value.toLowerCase())) {
        invalidEmailIndex = i;
      }
    });
    attributes.forEach((attribute, i) => {
      // Any attribute that is not part of the existing list of attributes is considered invalid.
      if (attribute.attributeId === 0) {
        invalidAttributeIndex = i;
      }

      // Check if the current attribute is numerical if the report type only allows numerical attributes.
      if (!allowsNonNumerical) {
        const foundAttribute = props.attributes.find(
          (attr) => attr.name.toLowerCase() === attribute.name.toLowerCase()
        );
        if (foundAttribute !== undefined && foundAttribute !== null) {
          if (
            foundAttribute.isBoolean ||
            (foundAttribute.regEnumId !== undefined && foundAttribute.regEnumId !== null)
          ) {
            invalidNumericalAttributeIndex = i;
          }
        }
      }
    });

    assetgroups.forEach((assetgroup, i) => {
      if (assetgroup.assetgroupId === 0) {
        invalidAssetgroupIndex = i;
      }
    });

    // Check the validity of each attribute's comparator if comparators are allowed.
    if (allowsComparators === true) {
      attributes.forEach((attribute, i) => {
        if (
          attribute.comparatorId === undefined ||
          attribute.comparatorId === null ||
          (attribute.comparatorId !== undefined && attribute.comparatorId !== null && attribute.comparatorId === 0)
        ) {
          invalidComparatorIndex = i;
        }
        if (attribute.value === undefined || attribute.value === null || isNaN(parseInt(attribute.value, 10))) {
          invalidThresholdValueIndex = i;
        }
      });
    }

    if (name.trim().length === 0) {
      setErrorMessage("A report is required to have a name.");
      return false;
    } else if (assetgroups.length === 0 && assetSelectionType === REPORT_ASSET_BY_GROUP) {
      setErrorMessage("A report must include at least one asset group.");
      return false;
    } else if (allowsAttributes === true && attributes.length === 0) {
      setErrorMessage("A report must include at least one attribute.");
      return false;
    } else if (scheduleSettings.length === 0) {
      setErrorMessage("The entered schedule is not valid.");
      return false;
    } else if (invalidEmailIndex >= 0) {
      setErrorMessage(`Email address #${invalidEmailIndex + 1} is invalid.`);
      return false;
    } else if (invalidAttributeIndex >= 0) {
      setErrorMessage(`Attribute #${invalidAttributeIndex + 1} is invalid.`);
      return false;
    } else if (invalidAssetgroupIndex >= 0) {
      setErrorMessage(`Asset group #${invalidAssetgroupIndex + 1} is invalid.`);
      return false;
    } else if (allowsAttributes && allowsComparators && invalidComparatorIndex >= 0) {
      setErrorMessage(`Comparator for attribute #${invalidComparatorIndex + 1} is invalid.`);
      return false;
    } else if (allowsAttributes && allowsComparators && invalidThresholdValueIndex >= 0) {
      setErrorMessage(`Threshold value for attribute #${invalidThresholdValueIndex + 1} is invalid.`);
      return false;
    } else if (allowsAttributes && !allowsNonNumerical && invalidNumericalAttributeIndex >= 0) {
      setErrorMessage(`Attribute #${invalidNumericalAttributeIndex + 1} is invalid because it is not numerical.`);
      return false;
    } else {
      return true;
    }
  }

  // Create a report.
  async function createReport(): Promise<void> {
    if (reportIsValid()) {
      const utcScheduleSettings = scheduleFromLocalToUtc(scheduleSettings);

      if (utcScheduleSettings !== null) {
        const attributeIds = attributes.map((attribute) => attribute.attributeId);
        const assetgroupIds = assetgroups.map((assetgroup) => assetgroup.assetgroupId);
        const emailAddressValues = emailAddresses.map((emailAddress) => emailAddress.value);

        // Only include attributes in the request if the report type allows attributes.
        let noDuplicateAttributeIds: number[] = [];
        if (allowsAttributes) {
          noDuplicateAttributeIds = attributeIds.filter(
            (attributeId, index) => attributeIds.indexOf(attributeId) === index
          );
        } else {
          noDuplicateAttributeIds = [];
        }

        const noDuplicateEmailAddressValues = emailAddressValues.filter(
          (emailAddressValue, index) => emailAddressValues.indexOf(emailAddressValue) === index
        );

        // Only include asset groups in the request if we selected the option for asset groups.
        let noDuplicateAssetgroupIds: number[] = [];
        if (assetSelectionType === REPORT_ASSET_BY_GROUP) {
          noDuplicateAssetgroupIds = assetgroupIds.filter(
            (assetgroupId, index) => assetgroupIds.indexOf(assetgroupId) === index
          );
        }

        // Build the thresholds map if any exist.
        const thresholds: ThresholdMap = {};
        if (allowsAttributes && allowsComparators) {
          attributes.forEach((attribute) => {
            const thresholdComparator = props.comparators.find(
              (comparator) => comparator.alertThresholdComparatorId === attribute.comparatorId
            );
            if (thresholdComparator !== undefined && thresholdComparator !== null) {
              thresholds[attribute.attributeId] = {
                comparator: thresholdComparator.name,
                value: attribute.value !== undefined ? parseInt(attribute.value, 10) : 0,
              };
            }
          });
        }

        // Send the correct asset selection type.
        const allAssets = assetSelectionType === REPORT_ASSET_ALL;

        const requestBody = {
          name: name.trim(),
          reportEnabled: enabled,
          reportType: type,
          hours: hours,
          selectedAttributeIds: noDuplicateAttributeIds,
          emailAddresses: noDuplicateEmailAddressValues,
          scheduleSettings: utcScheduleSettings,
          assetgroupIds: noDuplicateAssetgroupIds,
          allAssets: allAssets,
          isAssetRunning: isAssetRunning,
          thresholds: thresholds,
        };

        setLoading(true);
        const [response, responseBody] = (await apiRequest(
          `${API}/company/${currentUser.companyId}/report`,
          "POST",
          requestBody
        )) as [Response, PostResponseBody];
        setLoading(false);

        if (response.ok) {
          const reportDeepCopy = deepCopy(previousReport);
          reportDeepCopy.reportId = responseBody.reportId;
          reportDeepCopy.name = name;
          setPreviousReport(reportDeepCopy);
          props.onAction({
            type: REPORT_TYPES.CREATE_REPORT,
            payload: {
              report: reportDeepCopy,
            },
          });
          setShowConfirmExit(false);
          setErrorMessage("");
          props.onClose();
        } else {
          setErrorMessage(await getApiError(response, "Unable to create report."));
        }
      } else {
        setErrorMessage("Invalid date entered.");
      }
    }
  }

  // Edit a report.
  async function editReport(reportId: number): Promise<void> {
    if (reportIsValid()) {
      const utcScheduleSettings = scheduleFromLocalToUtc(scheduleSettings);

      if (utcScheduleSettings !== null) {
        const attributeIds = attributes.map((attribute) => attribute.attributeId);
        const assetgroupIds = assetgroups.map((assetgroup) => assetgroup.assetgroupId);
        const emailAddressValues = emailAddresses.map((emailAddress) => emailAddress.value);

        // Build the thresholds map if any exist.
        const thresholds: ThresholdMap = {};
        attributes.forEach((attribute) => {
          const thresholdComparator = props.comparators.find(
            (comparator) => comparator.alertThresholdComparatorId === attribute.comparatorId
          );
          if (thresholdComparator !== undefined && thresholdComparator !== null) {
            thresholds[attribute.attributeId] = {
              comparator: thresholdComparator.name,
              value: attribute.value !== undefined ? parseInt(attribute.value, 10) : 0,
            };
          }
        });

        const noDuplicateAttributeIds = attributeIds.filter(
          (attributeId, index) => attributeIds.indexOf(attributeId) === index
        );
        const noDuplicateEmailAddressValues = emailAddressValues.filter(
          (emailAddressValue, index) => emailAddressValues.indexOf(emailAddressValue) === index
        );

        // Only include asset groups in the request if we selected the option for asset groups.
        let noDuplicateAssetgroupIds: number[] = [];
        if (assetSelectionType === REPORT_ASSET_BY_GROUP) {
          noDuplicateAssetgroupIds = assetgroupIds.filter(
            (assetgroupId, index) => assetgroupIds.indexOf(assetgroupId) === index
          );
        }

        // Send the correct selection type.
        const allAssets = assetSelectionType === REPORT_ASSET_ALL;

        const requestBody = {
          name: name.trim(),
          reportEnabled: enabled,
          reportType: type,
          hours: hours,
          selectedAttributeIds: noDuplicateAttributeIds,
          emailAddresses: noDuplicateEmailAddressValues,
          scheduleSettings: utcScheduleSettings,
          assetgroupIds: noDuplicateAssetgroupIds,
          allAssets: allAssets,
          isAssetRunning: isAssetRunning,
          thresholds: thresholds,
        };

        setLoading(true);
        const [response] = (await apiRequest(
          `${API}/company/${currentUser.companyId}/report/${reportId}`,
          "PUT",
          requestBody
        )) as [Response, PutResponseBody];
        setLoading(false);

        if (response.ok) {
          const reportDeepCopy = deepCopy(previousReport);
          reportDeepCopy.reportId = reportId;
          reportDeepCopy.name = name;
          reportDeepCopy.isAssetRunning = isAssetRunning;
          setPreviousReport(reportDeepCopy);
          props.onAction({
            type: REPORT_TYPES.UPDATE_REPORT,
            payload: {
              report: reportDeepCopy,
            },
          });
          setErrorMessage("");
          props.onClose();
        } else {
          setErrorMessage(await getApiError(response, "Unable to update report."));
        }
      } else {
        setErrorMessage("Invalid date entered.");
      }
    }
  }

  // Delete a report.
  async function deleteReport(reportId: number): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/report/${reportId}`,
      "DELETE",
      null
    )) as [Response, DeleteResponseBody];
    setLoading(false);

    if (response.ok) {
      discardChanges();
      props.onAction({
        type: REPORT_TYPES.DELETE_REPORT,
        payload: {
          reportId: reportId,
        },
      });
    } else {
      setShowConfirmDelete(false);
      setErrorMessage(await getApiError(response, "Unable to delete report."));
    }
  }

  // Returns whether the current user is allowed to edit the form with their current permissions.
  function formIsEditable(): boolean {
    return (
      (props.isCreatingNewRecord && userHasPermission([[CREATE_REPORTS_PERMISSION]])) ||
      (!props.isCreatingNewRecord && userHasPermission([[UPDATE_REPORTS_PERMISSION]]))
    );
  }

  return (
    <div>
      <Spinner loading={loading} />

      <Modal
        show={true}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">{props.isCreatingNewRecord ? "Create Report" : "Edit Report"}</h5>
        </ModalHeader>

        <ModalBody>
          <div className="mx-2 mt-3">
            <label className="mb-3">Name</label>
            <input
              data-test="report-modal-name-input"
              className="form-control mx-auto mb-4"
              type="text"
              maxLength={MAX_REPORT_NAME_LENGTH}
              value={name}
              disabled={!formIsEditable()}
              onChange={(e) => setName(e.target.value)}
            />

            {/* Toggle email sending on or off. */}
            <div className={`${styles.switch} my-4`}>
              <div className="form-check form-switch">
                <input
                  className="form-check-input me-3"
                  type="checkbox"
                  checked={enabled}
                  disabled={!formIsEditable()}
                  onChange={() => setEnabled((prev) => !prev)}
                />
                {enabled ? (
                  <label onClick={() => setEnabled((prev) => !prev)}>
                    This report will be emailed at the scheduled times.
                  </label>
                ) : (
                  <label onClick={() => setEnabled((prev) => !prev)}>Sending emails is disabled for this report.</label>
                )}
              </div>
            </div>

            {/* Report type. */}
            <label className="mb-3">Report Type</label>
            {!userHasFullReportTypeAccess && (
              <InfoBanner message="Upgrade required for advanced reports. Please contact your system admin." />
            )}
            <select
              data-test="report-modal-report-type-select"
              className="form-select mb-4"
              value={type}
              disabled={!formIsEditable()}
              onChange={(e) => setType(e.target.value)}
            >
              {props.reportTypes !== undefined &&
                props.reportTypes !== null &&
                props.reportTypes.map((reportType) => (
                  <option key={reportType.reportTypeId} value={reportType.code} disabled={!reportType.userHasAccess}>
                    {reportType.description}
                  </option>
                ))}
            </select>

            {/* Select the window for the report range. */}
            {type === REPORT_MODE_DIFFERENCE && (
              <select
                className="form-select mb-5"
                value={String(hours)}
                disabled={!formIsEditable()}
                onChange={(e) => setHours(parseInt(e.target.value, 10))}
              >
                <option value="1">Difference over 1 hour.</option>
                <option value="6">Difference over 6 hours.</option>
                <option value="12">Difference over 12 hours.</option>
                <option value="24">Difference over 1 day.</option>
                <option value="72">Difference over 3 days.</option>
                <option value="168">Difference over 7 days.</option>
                {(userHasCrossCompanyAccess() || String(hours) === "720") && (
                  <option value="720">Difference over 30 days.</option>
                )}
              </select>
            )}

            {type !== REPORT_MODE_DIFFERENCE && type !== REPORT_MODE_CURRENT && (
              <select
                className="form-select mb-5"
                value={String(hours)}
                disabled={!formIsEditable()}
                onChange={(e) => setHours(parseInt(e.target.value, 10))}
              >
                <option value="1">1 hour time series.</option>
                <option value="6">6 hour time series.</option>
                <option value="12">12 hour time series.</option>
                <option value="24">1 day time series.</option>
                <option value="72">3 day time series.</option>
                <option value="168">7 day time series.</option>
                {(userHasCrossCompanyAccess() || String(hours) === "720") && (
                  <option value="720">30 day time series.</option>
                )}
              </select>
            )}

            <label className="mb-3">Assets to Report</label>
            <select
              data-test="report-modal-asset-to-report-select"
              className="form-select mb-4"
              value={assetSelectionType}
              disabled={!formIsEditable()}
              onChange={(e) => setAssetSelectionType(e.target.value)}
            >
              <option value={REPORT_ASSET_ALL}>Include all company assets in the report.</option>
              <option value={REPORT_ASSET_BY_GROUP}>Include assets from specific asset groups in the report.</option>
            </select>

            {/* Asset groups. */}
            {assetSelectionType === REPORT_ASSET_BY_GROUP && (
              <div className={styles.border}>
                <div className="mx-1">
                  <label className={styles.title}>
                    <span>Asset Groups&nbsp;</span>
                    <IconTooltip
                      id="asset-group-tooltip"
                      icon="info-circle"
                      message="The email that is sent out will include details about all assets in
                      any included asset groups."
                      color="var(--info-tooltip)"
                    />
                  </label>

                  <button
                    data-test="report-modal-add-asset-group-button"
                    type="submit"
                    className={`${styles.addButton} btn btn-success float-end`}
                    disabled={!formIsEditable()}
                    onClick={() => addAssetgroup()}
                  >
                    <span className="d-none d-sm-inline">Add Asset Group</span>
                    <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
                  </button>
                </div>

                {assetgroups.map((assetgroup, i) => (
                  <ReportAssetgroupForm
                    key={assetgroup.formId}
                    formIndex={i}
                    formId={assetgroup.formId}
                    name={assetgroup.name}
                    assetgroups={props.assetgroups}
                    disabled={!formIsEditable()}
                    onChange={(formId, name) => {
                      updateAssetgroup(formId, name);
                    }}
                    onDelete={(formId) => deleteAssetgroup(formId)}
                  />
                ))}
              </div>
            )}

            {/* Email addresses. */}
            <div className={styles.border}>
              <div className="mx-1">
                <label className={styles.title}>
                  <span>Email Addresses&nbsp;</span>
                  <IconTooltip
                    id="email-address-tooltip"
                    icon="info-circle"
                    message="This is a list of the email addresses that will receive this report."
                    color="var(--info-tooltip)"
                  />
                </label>

                <button
                  data-test="report-modal-add-email-button"
                  type="submit"
                  className={`${styles.addButton} btn btn-success float-end`}
                  disabled={!formIsEditable()}
                  onClick={() => addEmailAddress()}
                >
                  <span className="d-none d-sm-inline">Add Email Address</span>
                  <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
                </button>
              </div>

              {emailAddresses.map((emailAddress, i) => (
                <ReportEmailForm
                  key={emailAddress.formId}
                  formIndex={i}
                  formId={emailAddress.formId}
                  value={emailAddress.value}
                  disabled={!formIsEditable()}
                  onChange={(formId, value) => {
                    updateEmailAddress(formId, value);
                  }}
                  onDelete={(formId) => deleteEmailAddress(formId)}
                />
              ))}
            </div>

            {/* Attributes. */}
            {allowsAttributes && (
              <div className={styles.border}>
                <div className="mx-1">
                  <label className={styles.title}>
                    <span>Attributes&nbsp;</span>
                    <IconTooltip
                      id="attribute-tooltip"
                      icon="info-circle"
                      message={`The attributes selected will determine what data to show for each asset
                        on the report. A report is limited to containing at most ${MAX_REPORT_ATTRIBUTES}
                        attributes.`}
                      color="var(--info-tooltip)"
                    />
                  </label>

                  <button
                    data-test="report-modal-add-attribute-button"
                    type="submit"
                    className={`${styles.addButton} btn btn-success float-end`}
                    onClick={() => addAttribute()}
                    disabled={attributes.length >= MAX_REPORT_ATTRIBUTES || !formIsEditable()}
                  >
                    <span className="d-none d-sm-inline">Add Attribute</span>
                    <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
                  </button>
                </div>

                {attributes.map((attribute, i) => (
                  <ReportAttributeForm
                    key={attribute.formId}
                    formIndex={i}
                    formId={attribute.formId}
                    name={attribute.name}
                    comparatorId={attribute.comparatorId}
                    value={attribute.value}
                    attributes={getFilteredAttributes(type, props.reportTypes, props.attributes)}
                    comparators={props.comparators}
                    enumerationMap={props.enumerationMap}
                    disabled={!formIsEditable()}
                    allowsComparators={allowsComparators}
                    onChange={(formId, name, comparatorId, value) => {
                      updateAttribute(formId, name, comparatorId, value);
                    }}
                    onDelete={(formId) => deleteAttribute(formId)}
                  />
                ))}
              </div>
            )}

            {/* Schedule Settings */}
            <ScheduleForm
              isLiftStationReport={false}
              scheduleSettings={scheduleSettings}
              isTypeDisabled={false}
              disabled={!formIsEditable()}
              onChange={(scheduleSettings) => setScheduleSettings(scheduleSettings)}
            />

            {allowsAssetRunning && (
              <div className={`${styles.switch} form-check mx-1 my-4`}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isAssetRunning}
                  id="isAssetRunningCheckbox"
                  disabled={!formIsEditable()}
                  onChange={() => setIsAssetRunning((prev) => !prev)}
                />
                <label className="form-check-label" htmlFor="isAssetRunningCheckbox">
                  <span className="ms-3 me-2">Only report data when the asset is running.</span>
                  <IconTooltip
                    id="asset-running-report-tooltip"
                    icon="info-circle"
                    message={
                      "When checked, this will only include data that is reported while the attributes " +
                      "'Asset Running' and 'Power To Panel' are both reporting 'True'."
                    }
                    color="var(--info-tooltip)"
                  />
                </label>
              </div>
            )}

            {errorMessage.length > 0 && (
              <div className="mt-4">
                <Error message={errorMessage} />
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className={styles.footer}>
          {props.isCreatingNewRecord ? (
            <Fragment>
              <button
                data-test="report-modal-create-report-button"
                className={`${styles.btn} btn btn-primary`}
                type="button"
                onClick={() => createReport()}
              >
                Create Report
              </button>

              <button
                data-test="report-modal-cancel-button"
                className={`${styles.btn} btn btn-secondary`}
                type="button"
                onClick={() => exitModal()}
              >
                Cancel
              </button>
            </Fragment>
          ) : (
            <Fragment>
              {userHasPermission([[DELETE_REPORTS_PERMISSION]]) && (
                <button
                  data-test="report-modal-delete-button"
                  className={`${styles.btn} btn btn-danger me-auto`}
                  type="button"
                  onClick={() => setShowConfirmDelete(true)}
                >
                  Delete Report
                </button>
              )}

              {userHasPermission([[UPDATE_REPORTS_PERMISSION]]) && (
                <button
                  data-test="report-modal-save-changes-button"
                  className={`${styles.btn} btn btn-primary`}
                  type="button"
                  onClick={() => editReport(props.reportId)}
                >
                  Save Changes
                </button>
              )}

              <button
                data-test="report-modal-close-button"
                className={`${styles.btn} btn btn-secondary`}
                type="button"
                onClick={() => exitModal()}
              >
                Close
              </button>
            </Fragment>
          )}
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={showConfirmDelete}
        title={`Delete '${name}'`}
        content={`Are you sure that you want to delete the report '${name}'?`}
        yesText="Delete Report"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => deleteReport(props.reportId)}
        onNo={() => setShowConfirmDelete(false)}
      />

      <SaveChangesModal
        showModal={showConfirmExit}
        title="Changes have not been saved!"
        content="Are you sure that you want to exit without saving your changes?"
        onClose={() => setShowConfirmExit(false)}
        onSave={() => saveChanges()}
        onNoSave={() => discardChanges()}
      />
    </div>
  );
}

interface Props {
  isCreatingNewRecord: boolean;
  reportId: number;
  assetgroups: ReportAssetgroup[];
  attributes: Attribute[];
  reportTypes: ReportType[];
  comparators: Comparator[];
  enumerationMap: EnumerationMap;
  onClose: () => void;
  onAction: (action: Action) => void;
}

interface GetResponseBody {
  reportId: number;
  name: string;
  enabled: boolean;
  type: string;
  hours: number;
  attributes: Attribute[];
  assetgroups: ReportAssetgroup[];
  emailAddresses: string[];
  scheduleSettings: ReportSchedule[];
  allAssets: boolean[];
  isAssetRunning: boolean;
  thresholds: ThresholdMap;
}

interface PostResponseBody {
  reportId: number;
}

interface PutResponseBody {
  message: string;
}

interface DeleteResponseBody {
  message: string;
}

interface Action {
  type: string;
  payload: Payload;
}

interface Payload {
  reports?: AssetReport[];
  report?: AssetReport;
  reportId?: number;
}

interface AssetReport {
  reportId: number;
  name: string;
  enabled: boolean;
  type: string;
  hours: number;
  attributes: Attribute[];
  emailAddresses: string[];
  scheduleSettings: ReportSchedule[];
  assetgroups: ReportAssetgroup[];
  isAssetRunning: boolean;
  thresholds: ThresholdMap;
}

interface ReportSchedule {
  reportScheduleId: number;
  type: string;
  value: string;
}

interface ReportAssetgroup {
  assetgroupId: number;
  name: string;
}

interface FormAttribute {
  formId: number;
  attributeId: number;
  name: string;
  comparatorId?: number;
  value?: string;
}

interface FormAssetgroup {
  formId: number;
  assetgroupId: number;
  name: string;
}

interface FormEmailAddress {
  formId: number;
  value: string;
}

interface Attribute {
  attributeId: number;
  name: string;
  isBoolean: boolean;
  unitShortName?: string;
  regEnumId?: number;
}

interface ReportType {
  reportTypeId: number;
  code: string;
  description: string;
  userHasAccess: boolean;
  allowsAttributes: boolean;
  allowsComparators: boolean;
  allowsAssetRunning: boolean;
  allowsNonNumerical: boolean;
}

interface Comparator {
  alertThresholdComparatorId: number;
  name: string;
}

interface ThresholdMap {
  [attributeId: number]: Threshold;
}

interface Threshold {
  comparator: string;
  value: number;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}
